import type React from "react";
import { useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";

import type { PostTemplateDto } from "@/api/types";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { Switch } from "@/design-system/Switch";
import type { AdminCreateMassPostFormValues } from "@/modules/community-feed/pages/admin-create-post/AdminCreatePostPage";
import { AdminCreatePostFormAIHelper } from "@/modules/community-feed/pages/admin-create-post/components/AdminCreatePostFormAIHelper";
import { AdminCreatePostFormEditor } from "@/modules/community-feed/pages/admin-create-post/components/AdminCreatePostFormEditor";
import { AdminCreatePostFormScheduleSelector } from "@/modules/community-feed/pages/admin-create-post/components/AdminCreatePostFormScheduleSelector";
import { AdminCreatePostFormTemplateSelector } from "@/modules/community-feed/pages/admin-create-post/components/AdminCreatePostFormTemplateSelector";
import { InputFrame } from "@/modules/community-feed/pages/admin-create-post/components/InputFrame";
import { useTranslation } from "@/translations";

type Props = {
  templates: PostTemplateDto[];
  isErrorTemplates: boolean;
  templateInUse: PostTemplateDto | undefined;
  onApplyTemplate: (input: { template: PostTemplateDto; title: string; content: string }) => void;
  onClearTemplate: () => void;
  isUndoable: boolean;
  isRedoable: boolean;
  isCopilotAvailable: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
  onUndo: () => void;
  onRedo: () => void;
  audienceSelector: React.ReactNode;
  selectedScheduleDate: Date | undefined;
  onSelectScheduleDate: (date: Date | undefined) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  canConfigureComments: boolean;
};

/** Renders the feature-flagged progressive mass-message creation form. */
export function CreateMassPostRedesignForm({
  templates,
  isErrorTemplates,
  templateInUse,
  onApplyTemplate,
  onClearTemplate,
  isUndoable,
  isRedoable,
  isCopilotAvailable,
  isGenerating,
  onGenerate,
  onUndo,
  onRedo,
  audienceSelector,
  selectedScheduleDate,
  onSelectScheduleDate,
  onCancel,
  isSubmitting,
  canConfigureComments,
}: Props): React.ReactNode {
  const [isAudienceOpen, setIsAudienceOpen] = useState(false);
  const { t } = useTranslation();
  const form = useFormContext<AdminCreateMassPostFormValues>();
  const { errors } = useFormState({ control: form.control });
  const postType = useWatch({ control: form.control, name: "postType" });
  const contentError = errors.title?.message || errors.content?.message;
  const audienceError = errors.projects?.message;

  const continueFromContent = async () => {
    if (await form.trigger(["title", "content"])) {
      setIsAudienceOpen(true);
    }
  };

  const setCommentsEnabled = (enabled: boolean) => {
    form.setValue("postType", enabled ? "announcement" : "announcementReadOnly", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <Stack gap="lg">
      {!isErrorTemplates && (
        <AdminCreatePostFormTemplateSelector templates={templates} onApplyTemplate={onApplyTemplate} />
      )}

      <InputFrame
        title={t("page.admin-create-post.redesign.content.title")}
        error={contentError}
        action={
          templateInUse ? (
            <Button size="sm" variant="ghostPrimary" onClick={onClearTemplate}>
              {t("page.admin-create-post.section.step-2.btn.clear-template", {
                templateName: templateInUse.name,
              })}
            </Button>
          ) : undefined
        }
      >
        <Stack gap="md">
          <AdminCreatePostFormEditor
            contentAction={
              <AdminCreatePostFormAIHelper
                presentation="editor"
                isUndoable={isUndoable}
                isRedoable={isRedoable}
                isCopilotAvailable={isCopilotAvailable}
                isGenerating={isGenerating}
                onGenerate={onGenerate}
                onUndo={onUndo}
                onRedo={onRedo}
                isDisabled={Boolean(templateInUse)}
              />
            }
          />
          {!isAudienceOpen && (
            <Inline justify="end">
              <Button data-testid="continue-post-creation-btn" onClick={() => void continueFromContent()}>
                {t("common.action.continue")}
              </Button>
            </Inline>
          )}
        </Stack>
      </InputFrame>

      <InputFrame
        title={t("page.admin-create-post.redesign.audience.title")}
        description={t("page.admin-create-post.redesign.audience.description")}
        isOpen={isAudienceOpen || Boolean(audienceError)}
        error={audienceError}
        onOpen={() => setIsAudienceOpen(true)}
        toggleTestId="post-audience-section-btn"
        isDisabled={false}
      >
        <Stack gap="xl">
          <Stack gap="md">
            {audienceSelector}
            <Inline gap="sm" align="center">
              <FormCheckbox
                label={t("page.admin-create-post.form.email-preview-only.label")}
                name="isEmailPreviewOnly"
              />
              <Info tooltip={t("page.admin-create-post.form.email-preview-only.info")} />
            </Inline>
          </Stack>
          {canConfigureComments && (
            <Box padding={{ top: "md" }}>
              <Card density="compact">
                <Spread space="md" align="center">
                  <Stack gap="xs">
                    <span className="text-body-emphasis">{t("page.admin-create-post.redesign.comments.label")}</span>
                    <span className="text-caption text-text-secondary">
                      {t("page.admin-create-post.redesign.comments.description")}
                    </span>
                  </Stack>
                  <Switch
                    data-testid="allow-comments-switch"
                    ariaLabel={t("page.admin-create-post.redesign.comments.label")}
                    isChecked={postType === "announcement"}
                    onChange={(event) => setCommentsEnabled(event.target.checked)}
                  />
                </Spread>
              </Card>
            </Box>
          )}
          <Spread space="sm" align="center">
            <Button variant="subtle" tone="error" onClick={onCancel}>
              {t("common.action.cancel")}
            </Button>
            <Inline gap="sm" align="center">
              <AdminCreatePostFormScheduleSelector
                selectedDate={selectedScheduleDate}
                onSelectDate={onSelectScheduleDate}
              />
              <Button type="submit" isLoading={isSubmitting}>
                {selectedScheduleDate
                  ? t("page.admin-create-mass-message.form.btn.cta-schedule")
                  : t("page.admin-create-mass-message.form.btn.cta-post")}
              </Button>
            </Inline>
          </Spread>
        </Stack>
      </InputFrame>
    </Stack>
  );
}
